import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { XIcon } from "@phosphor-icons/react";
import { useModalAccessibility } from "../../hooks/use_modal_accessibility";
import AuthCard, { type AuthReason } from "./auth_card";
import type { AuthFlow } from "./auth_flow";

// Above the ask-question modal and other dialogs it can be opened from.
const BACKDROP_CLASS = [
	"fixed inset-0 z-[100001] flex items-center justify-center bg-black/40 p-4",
	"max-[480px]:p-0 max-[480px]:items-stretch",
].join(" ");

const CARD_CLASS = [
	"relative w-full max-w-[420px] max-h-full overflow-y-auto font-sans",
	"rounded-[12px] bg-auth-card text-auth-text p-7",
	"shadow-[0_24px_48px_-24px_rgba(26,26,46,.35)]",
	"max-[480px]:max-w-none max-[480px]:rounded-none max-[480px]:p-5 max-[480px]:pt-14",
].join(" ");

const CLOSE_CLASS = [
	"absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full border-0",
	"bg-transparent text-auth-sub cursor-pointer hover:bg-auth-bg hover:text-auth-text",
].join(" ");

interface Props {
	initial: AuthFlow;
	reason: AuthReason;
	returnTo: string;
	/** "light" keeps the card light whatever the site theme (the landing). */
	appearance: "site" | "light";
	onSignedIn: (token: string) => void;
	onLeaveForGoogle?: () => void;
	/** Gets the flow as it was, or null once signed in. */
	onClose: (flow: AuthFlow | null) => void;
}

const AuthModal = ({
	initial,
	reason,
	returnTo,
	appearance,
	onSignedIn,
	onLeaveForGoogle,
	onClose,
}: Props) => {
	const { t } = useTranslation();
	const [flow, setFlow] = useState<AuthFlow>(initial);
	const close = useCallback(() => onClose(flow), [onClose, flow]);
	const dialogRef = useModalAccessibility(true, close);

	// Lives at the app root: leaving the page (Back, a link) closes it the way the cross does.
	const { pathname } = useLocation();
	const openedOn = useRef(pathname);
	useEffect(() => {
		if (pathname !== openedOn.current) close();
	}, [pathname, close]);

	return createPortal(
		// Only the cross and Escape close it: a stray click outside must not cost a code the reader waits for.
		<div
			className={`${BACKDROP_CLASS} ${appearance === "light" ? "auth-light" : ""}`}
			data-testid="auth-backdrop"
		>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="auth-card-title"
				className={CARD_CLASS}
			>
				<button type="button" aria-label={t("auth.close")} className={CLOSE_CLASS} onClick={close}>
					<XIcon size={18} />
				</button>
				<AuthCard
					flow={flow}
					setFlow={setFlow}
					reason={reason}
					returnTo={returnTo}
					onSignedIn={onSignedIn}
					onLeaveForGoogle={onLeaveForGoogle}
				/>
			</div>
		</div>,
		document.body,
	);
};

export default AuthModal;
